import { useLoaderData, useOutletContext, useRevalidator } from "react-router-dom";
import { ContextType } from "../../ProjectWrapper";
import React, { useContext, useEffect, useState } from "react";
import { SnackbarContext } from "../../../../components/Snackbar/SnackbarProvider";
import { ApiClient } from "../../../../client/ApiClient";
import { Width } from "../../../../const/Width";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  useTheme
} from "@mui/material";
import { SettingsHead } from "../../../../components/form/SettingsHead";
import { SettingsOptionWrapper, SettingsSwitchOptionWrapper } from "../../../../components/form/OptionsWrapper";
import { FormButtons } from "../../../../components/form/FormButtons";
import { SessionTokenCustomization } from "./SessionTokenCustomization";
import { Subscription } from "../../../../models/Subscription";

interface State {
  isLoading: boolean
  sessionDuration: number
  session_duration_unit: string
  serverSideSessionLimit: number
  cookie_retention: "session" | "persistent" | "prompt"
  allow_revocation: boolean
  acquire_ip_address: boolean
  acquire_user_agent: boolean
  show_on_profile: boolean
  idle_timeout: number
  idle_timeout_unit: string
}

interface LoaderData {
  subscription: Subscription | null
}

export const Session = () => {
  const { project, organization } = useOutletContext() as ContextType
  const {subscription} = useLoaderData() as LoaderData
  const revalidator = useRevalidator()
  const theme = useTheme()

  const parseDuration = (durationStr: string): { duration: number; unit: string } => {
    if (!durationStr || durationStr === "0") {
      return { duration: 0, unit: "m" }
    }

    // Parse Go duration format (e.g., "30m", "1h45m", "24h", "1440m")
    let totalMinutes = 0
    
    // Extract hours
    const hoursMatch = durationStr.match(/(\d+)h/)
    if (hoursMatch) {
      totalMinutes += parseInt(hoursMatch[1]) * 60
    }
    
    // Extract minutes
    const minutesMatch = durationStr.match(/(\d+)m/)
    if (minutesMatch) {
      totalMinutes += parseInt(minutesMatch[1])
    }
    
    // Extract seconds (convert to minutes, rounded)
    const secondsMatch = durationStr.match(/(\d+)s/)
    if (secondsMatch) {
      totalMinutes += Math.round(parseInt(secondsMatch[1]) / 60)
    }
    
    if (totalMinutes === 0) {
      return { duration: 0, unit: "m" }
    }
    
    // Choose the most appropriate unit
    if (totalMinutes % (365 * 24 * 60) === 0) {
      return { duration: totalMinutes / (365 * 24 * 60), unit: "y" }
    } else if (totalMinutes % (24 * 60) === 0) {
      return { duration: totalMinutes / (24 * 60), unit: "d" }
    } else if (totalMinutes % 60 === 0) {
      return { duration: totalMinutes / 60, unit: "h" }
    } else {
      return { duration: totalMinutes, unit: "m" }
    }
  }

  const parsedIdleTimeout = parseDuration(project.session?.idle_timeout ?? "0")
  const parsedSessionDuration = parseDuration(project.session?.lifespan ?? "1h")

  const [state, setState] = useState<State>({
    isLoading: false,
    sessionDuration: parsedSessionDuration.duration,
    session_duration_unit: parsedSessionDuration.unit,
    serverSideSessionLimit: project.session?.limit ?? 5,
    cookie_retention: project.session?.cookie_retention ?? "persistent",
    allow_revocation: project.session?.allow_revocation ?? true,
    acquire_ip_address: project.session?.acquire_ip_address ?? true,
    acquire_user_agent: project.session?.acquire_user_agent ?? true,
    show_on_profile: project.session?.show_on_profile ?? true,
    idle_timeout: parsedIdleTimeout.duration,
    idle_timeout_unit: parsedIdleTimeout.unit
  })
  const { error, success } = useContext(SnackbarContext)

  const minutes = (duration: number, unit: string): number => {
    if (isNaN(duration) || duration === 0) {
      return 0
    }

    // Convert to minutes
    let minutes = duration
    switch (unit) {
      case "h":
        minutes = duration * 60
        break
      case "d":
        minutes = duration * 24 * 60
        break
      case "y":
        minutes = duration * 365 * 24 * 60
        break
      case "m":
      default:
        minutes = duration
        break
    }

    // Round to full minutes
    return Math.round(minutes)
  }

  const formatDuration = (duration: number, unit: string): string => {

    // Round to full minutes
    const roundedMinutes = minutes(duration, unit)
    // Format in the most appropriate Go duration format
    if (roundedMinutes === 0) {
      return "0m"
    }
    
    // If >= 60 minutes, format with hours and remaining minutes
    if (roundedMinutes >= 60) {
      const hours = Math.floor(roundedMinutes / 60)
      const remainingMinutes = roundedMinutes % 60
      
      if (remainingMinutes === 0) {
        return `${hours}h`
      } else {
        return `${hours}h${remainingMinutes}m`
      }
    }
    
    // Otherwise use minutes only
    return `${roundedMinutes}m`
  }

  const somethingChanged = () => {
    return (project.session?.lifespan ?? "1h") !== formatDuration(state.sessionDuration, state.session_duration_unit) ||
      (project.session?.limit ?? 5) !== state.serverSideSessionLimit ||
      (project.session?.cookie_retention ?? "persistent") !== state.cookie_retention ||
      (project.session?.allow_revocation ?? true) !== state.allow_revocation ||
      (project.session?.acquire_ip_address ?? true) !== state.acquire_ip_address ||
      (project.session?.acquire_user_agent ?? true) !== state.acquire_user_agent ||
      (project.session?.show_on_profile ?? true) !== state.show_on_profile ||
      (project.session?.idle_timeout ?? "0") !== formatDuration(state.idle_timeout, state.idle_timeout_unit)
  }

  const onSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault()
    setState({ ...state, isLoading: true })
    if (minutes(state.sessionDuration, state.session_duration_unit) > 43200 && !hasSubscription) {
      error("Please upgrade to pro to set a session duration greater one month")
      return
    }
    try {
      await ApiClient.updateProject(organization.id, project.id, {
        session: {
          lifespan: formatDuration(state.sessionDuration, state.session_duration_unit),
          cookie_retention: state.cookie_retention,
          limit: state.serverSideSessionLimit,
          allow_revocation: state.allow_revocation,
          acquire_ip_address: state.acquire_ip_address,
          acquire_user_agent: state.acquire_user_agent,
          show_on_profile: state.show_on_profile,
          idle_timeout: formatDuration(state.idle_timeout, state.idle_timeout_unit),
        },
      })
      revalidator.revalidate()
      success("Settings updated")
    } catch (e) {
      error("failed to update settings")
    }
    setState({ ...state, isLoading: false })
  }

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.type === "checkbox") {
      setState({
        ...state,
        [e.target.name]: e.target.checked,
      })
    } else if (e.target.type === "number") {
      setState({
        ...state,
        [e.target.name]: e.target.valueAsNumber,
      })
    } else {
      setState({
        ...state,
        [e.target.name]: e.target.value,
      })
    }
  }

  const onSelectChange = (e: SelectChangeEvent) => {
    setState({
      ...state,
      [e.target.name]: e.target.value,
    })
  }

  const onCancel = () => {
    setState({
      ...state,
      sessionDuration: parsedSessionDuration.duration,
      session_duration_unit: parsedSessionDuration.unit,
      serverSideSessionLimit: project.session?.limit ?? 5,
      cookie_retention: project.session?.cookie_retention ?? "persistent",
      allow_revocation: project.session?.allow_revocation ?? true,
      acquire_ip_address: project.session?.acquire_ip_address ?? true,
      acquire_user_agent: project.session?.acquire_user_agent ?? true,
      show_on_profile: project.session?.show_on_profile ?? true,
      idle_timeout: parsedIdleTimeout.duration,
      idle_timeout_unit: parsedIdleTimeout.unit,
    })
  }

  useEffect(() => {
    const sessionDuration = parseDuration(project.session?.lifespan ?? "1h")
    const idleTimeout = parseDuration(project.session?.idle_timeout ?? "0")
    setState({
      ...state,
      sessionDuration: sessionDuration.duration,
      session_duration_unit: sessionDuration.unit,
      idle_timeout: idleTimeout.duration,
      idle_timeout_unit: idleTimeout.unit,
    })
  }, [project])

  const hasSubscription = subscription !== null && subscription.is_active

  return <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    maxWidth: Width.NORMAL,
    gap: 5
  }}>
    <form onSubmit={onSubmit}>
      <SettingsSwitchOptionWrapper sx={{
        borderBottom: `1px solid ${theme.palette.grey["900"]}`
      }}>
        <SettingsHead description="Configure session-related settings." title="Session" sx={{ mb: 1 }} />
      </SettingsSwitchOptionWrapper>
      <SettingsOptionWrapper>
        <SettingsHead title="Session duration" description="Set how long session JWTs will be valid."
                      size="small" sx={{ mb: 0 }} />
        <Stack direction="row" sx={{ width: { xs: '100%', md: 'unset' }}}>
          <TextField fullWidth label="Session duration"
                     type="number"
                     name="sessionDuration"
                     size="small"
                     onChange={onChange}
                     value={state.sessionDuration}
                     inputProps={{ min: 0, step: 0.01 }}
                     InputProps={{
                       sx: {
                         borderTopRightRadius: 0,
                         borderBottomRightRadius: 0
                       }}}
                     sx={{
                       maxWidth: { xs: '100%', md: '220px' },
                       '&.MuiInputBase-root': {
                         borderTopRightRadius: 0,
                         borderBottomRightRadius: 0
                       }
                     }}
          />
          <Select name="session_duration_unit" displayEmpty size="small" value={state.session_duration_unit} onChange={onSelectChange} sx={{
            '&.MuiInputBase-root': {
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            }
          }}>
            <MenuItem value="m">Minutes</MenuItem>
            <MenuItem value="h">Hours</MenuItem>
            <MenuItem value="d">Days</MenuItem>
            <MenuItem value="y">Years</MenuItem>
          </Select>
        </Stack>
      </SettingsOptionWrapper>
      <SettingsOptionWrapper>
        <SettingsHead title="Idle timeout" description="How long a user can be inactive before being logged out."
                      size="small" sx={{ mb: 0 }} plan="Pro" type="feature" hasActiveSubscription={hasSubscription} organization={organization} />
        <Stack direction="row" sx={{ width: { xs: '100%', md: 'unset' }}}>
          <TextField fullWidth label="Idle timeout"
                     type="number"
                     name="idle_timeout"
                     size="small"
                     onChange={onChange}
                     value={state.idle_timeout}
                     disabled={!hasSubscription}
                     inputProps={{ min: 0, step: 0.01 }}
                     InputProps={{
                       sx: {
                         borderTopRightRadius: 0,
                         borderBottomRightRadius: 0
                     }}}
                     sx={{
                       maxWidth: { xs: '100%', md: '220px' },
                       '&.MuiInputBase-root': {
                         borderTopRightRadius: 0,
                         borderBottomRightRadius: 0
                       }
                     }}
          />
          <Select name="idle_timeout_unit" displayEmpty size="small" value={state.idle_timeout_unit} onChange={onSelectChange} disabled={!hasSubscription} sx={{
            '&.MuiInputBase-root': {
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            }
          }}>
            <MenuItem value="m">Minutes</MenuItem>
            <MenuItem value="h">Hours</MenuItem>
            <MenuItem value="d">Days</MenuItem>
            <MenuItem value="y">Years</MenuItem>
          </Select>
        </Stack>
      </SettingsOptionWrapper>
      <SettingsOptionWrapper>
        <SettingsHead title="Stay signed in (cookie type)"
                      description="Hanko cookies can be set to be either persistent or session cookies."
                      helpText="Session cookies are typically deleted from the browser when the browser window is closed, whereas the user remains logged in for the session duration with persistent cookies. With 'prompt', Hanko Elements displays a 'Stay signed in' checkbox on the login form and lets the user decide."
                      size="small" sx={{ mb: 0 }} minElementsVersion="1.3" />
        <FormControl fullWidth sx={{ maxWidth: { xs: '100%', md: '220px' } }} size="small">
          <InputLabel id="cookie_retention">Cookie type</InputLabel>
          <Select labelId="cookie_retention" id="cookie_retention_select"
                  name="cookie_retention" label="Cookie type" onChange={onSelectChange}
                  value={state.cookie_retention}>
            <MenuItem key="session" value="session">Session</MenuItem>
            <MenuItem key="persistent" value="persistent">Persistent</MenuItem>
            <MenuItem key="prompt" value="prompt">Prompt</MenuItem>
          </Select>
        </FormControl>
      </SettingsOptionWrapper>
      <SettingsSwitchOptionWrapper>
        <SettingsHead
          description="Allow the user to remotely revoke their session in their profile."
          title="Allow session revocation" minElementsVersion="1.3"
          sx={{ mb: 1 }} size="small" />
        <Switch name="allow_revocation" color="success" checked={state.allow_revocation}
                onChange={onChange} />
      </SettingsSwitchOptionWrapper>
      <SettingsSwitchOptionWrapper>
        <SettingsHead
          description="Acquire the IP address of the user and show it along side the session."
          title="Acquire IP address" minElementsVersion="1.3"
          sx={{ mb: 1 }} size="small" />
        <Switch name="acquire_ip_address" color="success" checked={state.acquire_ip_address}
                onChange={onChange} />
      </SettingsSwitchOptionWrapper>
      <SettingsSwitchOptionWrapper>
        <SettingsHead
          description="Acquire the user agent of the user and show it along side the session."
          title="Acquire user agent" minElementsVersion="1.3"
          sx={{ mb: 1 }} size="small" />
        <Switch name="acquire_user_agent" color="success" checked={state.acquire_user_agent}
                onChange={onChange} />
      </SettingsSwitchOptionWrapper>
      <SettingsSwitchOptionWrapper>
        <SettingsHead
          description="Show a list of active sessions in the profile."
          title="Show sessions on profile" minElementsVersion="1.3"
          sx={{ mb: 1 }} size="small" />
        <Switch name="show_on_profile" color="success" checked={state.show_on_profile}
                onChange={onChange} />
      </SettingsSwitchOptionWrapper>
      <SettingsOptionWrapper>
        <SettingsHead title="Session limit"
                      description="Controls how many active sessions a user can have at the same time. The oldest session becomes invalid if a new session is created above the limit."
                      size="small" sx={{ mb: 0 }} />
        <TextField fullWidth label="Session limit" sx={{ maxWidth: { xs: '100%', md: '220px' } }}
                   type="number"
                   inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min: 1, max: 100 }}
                   name="serverSideSessionLimit" size="small" onChange={onChange}
                   value={state.serverSideSessionLimit} />
      </SettingsOptionWrapper>
      <FormButtons onCancel={onCancel} loading={state.isLoading} disabled={!somethingChanged()} />
    </form>
    <SessionTokenCustomization organization={organization} project={project} />
  </Box>
}